'use client';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useChat } from '@/lib/hooks/useChat';
import toast from 'react-hot-toast';
import { MessageCircle, X, Send, Mic, MicOff, Volume2, VolumeX, Globe, ChevronDown, Bot } from 'lucide-react';

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
}

const SYSTEM_PROMPT_EN = `You are ASHA Assistant, a helpful and compassionate AI assistant for ASHA Recovery Group — a network of physical therapy and rehabilitation clinics in the Chicagoland area.

Your role is to:
- Answer questions about ASHA's services (Physical Therapy, Occupational Therapy, Sports Injury Rehab, Work Injury Therapy, Pain Management, Post-Surgical Rehabilitation)
- Help patients understand what to expect at their first appointment
- Provide information about insurance coverage and billing
- Guide patients to the right clinic location
- Explain the referral process for physicians
- Provide general rehabilitation and recovery information

Key facts about ASHA Recovery Group:
- 18 clinic locations across Chicagoland
- Phone: (312) 555-0100
- Email: info@asharecoverygroup.com
- Website: asharecoverygroup.com
- JCAHO Accredited
- 94.2% patient recovery rate
- 120+ licensed therapists
- Extended hours: 6:30 AM – 8:00 PM (varies by location)
- Accepts most major insurance: BCBS, Aetna, Cigna, UnitedHealthcare, Medicare, Medicaid, Workers' Comp

Always be warm, professional, and empathetic. If you don't know something specific, direct the patient to call (312) 555-0100 or email info@asharecoverygroup.com. Keep responses concise and helpful.`;

const SYSTEM_PROMPT_ES = `Eres el Asistente ASHA, un asistente de IA útil y compasivo para ASHA Recovery Group — una red de clínicas de fisioterapia y rehabilitación en el área de Chicagoland.

Tu función es:
- Responder preguntas sobre los servicios de ASHA (Fisioterapia, Terapia Ocupacional, Rehabilitación de Lesiones Deportivas, Terapia de Lesiones Laborales, Manejo del Dolor, Rehabilitación Post-Quirúrgica)
- Ayudar a los pacientes a entender qué esperar en su primera cita
- Proporcionar información sobre cobertura de seguro y facturación
- Guiar a los pacientes a la clínica correcta
- Explicar el proceso de referidos para médicos
- Proporcionar información general sobre rehabilitación y recuperación

Datos clave sobre ASHA Recovery Group:
- 18 ubicaciones de clínicas en todo Chicagoland
- Teléfono: (312) 555-0100
- Correo: info@asharecoverygroup.com
- Sitio web: asharecoverygroup.com
- Acreditado por JCAHO
- Tasa de recuperación del 94.2%
- Más de 120 terapeutas licenciados
- Horario extendido: 6:30 AM – 8:00 PM (varía según la ubicación)
- Acepta la mayoría de los seguros principales: BCBS, Aetna, Cigna, UnitedHealthcare, Medicare, Medicaid, Compensación Laboral

Sé siempre cálido, profesional y empático. Si no sabes algo específico, dirige al paciente a llamar al (312) 555-0100 o enviar un correo a info@asharecoverygroup.com. Mantén las respuestas concisas y útiles.`;

const WELCOME_EN = "Hello! I'm ASHA Assistant. How can I help you with your recovery journey today?";
const WELCOME_ES = "¡Hola! Soy el Asistente ASHA. ¿Cómo puedo ayudarte con tu proceso de recuperación hoy?";

type Language = 'en' | 'es';

const UI_TEXT = {
  en: {
    title: 'ASHA Assistant',
    subtitle: 'AI-Powered Support',
    placeholder: 'Ask about services, locations, insurance...',
    send: 'Send',
    listening: 'Listening...',
    voiceOn: 'Voice input on',
    voiceOff: 'Voice input off',
    speakOn: 'Text-to-speech on',
    speakOff: 'Text-to-speech off',
    lang: 'EN',
    langFull: 'English',
    thinking: 'ASHA is thinking...',
    openChat: 'Open ASHA Assistant',
    closeChat: 'Close ASHA Assistant',
  },
  es: {
    title: 'Asistente ASHA',
    subtitle: 'Soporte con IA',
    placeholder: 'Pregunta sobre servicios, ubicaciones, seguro...',
    send: 'Enviar',
    listening: 'Escuchando...',
    voiceOn: 'Voz activada',
    voiceOff: 'Voz desactivada',
    speakOn: 'Lectura en voz alta activada',
    speakOff: 'Lectura en voz alta desactivada',
    lang: 'ES',
    langFull: 'Español',
    thinking: 'ASHA está pensando...',
    openChat: 'Abrir Asistente ASHA',
    closeChat: 'Cerrar Asistente ASHA',
  },
};

export default function AshaChatbot() {
  const [isOpen, setIsOpen] = useState(false);
  const [input, setInput] = useState('');
  const [language, setLanguage] = useState<Language>('en');
  const [messages, setMessages] = useState<Message[]>([]);
  const [isListening, setIsListening] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [ttsEnabled, setTtsEnabled] = useState(false);
  const [speechSupported, setSpeechSupported] = useState(false);
  const [ttsSupported, setTtsSupported] = useState(false);
  const [hasNewMessage, setHasNewMessage] = useState(false);
  const [conversationHistory, setConversationHistory] = useState<Array<{ role: string; content: string }>>([]);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const recognitionRef = useRef<SpeechRecognition | null>(null);
  const utteranceRef = useRef<SpeechSynthesisUtterance | null>(null);

  const { response, isLoading, error, sendMessage } = useChat(
    'GEMINI',
    'gemini/gemini-2.5-flash',
    true
  );

  const ui = UI_TEXT[language];

  // Initialize speech support
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
      setSpeechSupported(!!SpeechRecognition);
      setTtsSupported('speechSynthesis' in window);
    }
  }, []);

  // Set welcome message when opened or language changes
  useEffect(() => {
    if (isOpen && messages.length === 0) {
      setMessages([{
        id: 'welcome',
        role: 'assistant',
        content: language === 'en' ? WELCOME_EN : WELCOME_ES,
        timestamp: new Date(),
      }]);
    }
  }, [isOpen]);

  // Handle language switch — reset conversation
  const handleLanguageSwitch = useCallback(() => {
    const newLang: Language = language === 'en' ? 'es' : 'en';
    setLanguage(newLang);
    setConversationHistory([]);
    setMessages([{
      id: 'welcome-' + newLang,
      role: 'assistant',
      content: newLang === 'en' ? WELCOME_EN : WELCOME_ES,
      timestamp: new Date(),
    }]);
  }, [language]);

  // Handle AI response
  useEffect(() => {
    if (error) toast.error(error.message);
  }, [error]);

  useEffect(() => {
    if (response && !isLoading) {
      const assistantMsg: Message = {
        id: `assistant-${Date.now()}`,
        role: 'assistant',
        content: response,
        timestamp: new Date(),
      };
      setMessages(prev => {
        // Remove any streaming placeholder and add final
        const filtered = prev.filter(m => m.id !== 'streaming');
        return [...filtered, assistantMsg];
      });
      setConversationHistory(prev => [...prev, { role: 'assistant', content: response }]);

      if (ttsEnabled && ttsSupported) {
        speakText(response);
      }

      if (!isOpen) setHasNewMessage(true);
    }
  }, [response, isLoading]);

  // Streaming placeholder
  useEffect(() => {
    if (isLoading && response) {
      setMessages(prev => {
        const filtered = prev.filter(m => m.id !== 'streaming');
        return [...filtered, {
          id: 'streaming',
          role: 'assistant' as const,
          content: response,
          timestamp: new Date(),
        }];
      });
    }
  }, [response, isLoading]);

  // Auto-scroll
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus input when opened
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 100);
      setHasNewMessage(false);
    }
  }, [isOpen]);

  const speakText = useCallback((text: string) => {
    if (!ttsSupported) return;
    window.speechSynthesis.cancel();
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = language === 'es' ? 'es-US' : 'en-US';
    utterance.rate = 0.95;
    utterance.pitch = 1;
    utteranceRef.current = utterance;
    utterance.onstart = () => setIsSpeaking(true);
    utterance.onend = () => setIsSpeaking(false);
    utterance.onerror = () => setIsSpeaking(false);
    window.speechSynthesis.speak(utterance);
  }, [language, ttsSupported]);

  const stopSpeaking = useCallback(() => {
    if (ttsSupported) {
      window.speechSynthesis.cancel();
      setIsSpeaking(false);
    }
  }, [ttsSupported]);

  const startListening = useCallback(() => {
    if (!speechSupported) return;
    const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    const recognition = new SpeechRecognition();
    recognition.lang = language === 'es' ? 'es-US' : 'en-US';
    recognition.interimResults = false;
    recognition.maxAlternatives = 1;

    recognition.onresult = (event: SpeechRecognitionEvent) => {
      const transcript = event.results[0][0].transcript;
      setInput(transcript);
      setIsListening(false);
    };
    recognition.onerror = () => setIsListening(false);
    recognition.onend = () => setIsListening(false);

    recognitionRef.current = recognition;
    recognition.start();
    setIsListening(true);
  }, [language, speechSupported]);

  const stopListening = useCallback(() => {
    recognitionRef.current?.stop();
    setIsListening(false);
  }, []);

  const handleSend = useCallback(async () => {
    const trimmed = input.trim();
    if (!trimmed || isLoading) return;

    const userMsg: Message = {
      id: `user-${Date.now()}`,
      role: 'user',
      content: trimmed,
      timestamp: new Date(),
    };

    const newHistory = [...conversationHistory, { role: 'user', content: trimmed }];
    setConversationHistory(newHistory);
    setMessages(prev => [...prev, userMsg]);
    setInput('');

    const systemPrompt = language === 'es' ? SYSTEM_PROMPT_ES : SYSTEM_PROMPT_EN;
    const apiMessages = [
      { role: 'system', content: systemPrompt },
      ...newHistory,
    ];

    sendMessage(apiMessages, { temperature: 0.7, max_tokens: 600 });
  }, [input, isLoading, conversationHistory, language, sendMessage]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString(language === 'es' ? 'es-US' : 'en-US', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <>
      {/* Floating Button */}
      <div className="fixed bottom-6 right-6 z-50 flex flex-col items-end gap-3">
        {/* Chat Window */}
        {isOpen && (
          <div
            className="w-[380px] max-w-[calc(100vw-2rem)] rounded-3xl overflow-hidden flex flex-col"
            style={{
              height: '560px',
              background: 'white',
              boxShadow: '0 25px 60px rgba(10,42,67,0.25), 0 8px 20px rgba(10,42,67,0.15)',
              border: '1px solid rgba(27,175,191,0.2)',
            }}
            role="dialog"
            aria-label={ui.title}
            aria-modal="true"
          >
            {/* Header */}
            <div
              className="flex items-center justify-between px-5 py-4 flex-shrink-0"
              style={{ background: 'linear-gradient(135deg, var(--primary) 0%, #0d3a5c 100%)' }}
            >
              <div className="flex items-center gap-3">
                <div
                  className="w-10 h-10 rounded-2xl flex items-center justify-center flex-shrink-0"
                  style={{ background: 'rgba(27,175,191,0.25)' }}
                >
                  <Bot size={20} color="#1BAFBF" />
                </div>
                <div>
                  <p className="text-white font-bold text-sm leading-tight">{ui.title}</p>
                  <p className="text-white/60 text-xs">{ui.subtitle}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {/* Language Toggle */}
                <button
                  onClick={handleLanguageSwitch}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-bold transition-all duration-150 hover:bg-white/20"
                  style={{ background: 'rgba(255,255,255,0.12)', color: 'white' }}
                  aria-label={`Switch to ${language === 'en' ? 'Spanish' : 'English'}`}
                  title={`Switch to ${language === 'en' ? 'Español' : 'English'}`}
                >
                  <Globe size={13} />
                  {ui.lang}
                </button>
                {/* TTS Toggle */}
                {ttsSupported && (
                  <button
                    onClick={() => {
                      if (isSpeaking) stopSpeaking();
                      setTtsEnabled(prev => !prev);
                    }}
                    className="w-8 h-8 rounded-xl flex items-center justify-center transition-all duration-150 hover:bg-white/20"
                    style={{ background: ttsEnabled ? 'rgba(27,175,191,0.3)' : 'rgba(255,255,255,0.1)', color: 'white' }}
                    aria-label={ttsEnabled ? ui.speakOff : ui.speakOn}
                    title={ttsEnabled ? ui.speakOff : ui.speakOn}
                  >
                    {ttsEnabled ? <Volume2 size={15} /> : <VolumeX size={15} />}
                  </button>
                )}
                {/* Close */}
                <button
                  onClick={() => setIsOpen(false)}
                  className="w-8 h-8 rounded-xl flex items-center justify-center transition-all duration-150 hover:bg-white/20"
                  style={{ background: 'rgba(255,255,255,0.1)', color: 'white' }}
                  aria-label={ui.closeChat}
                >
                  <X size={16} />
                </button>
              </div>
            </div>

            {/* Messages */}
            <div
              className="flex-1 overflow-y-auto px-4 py-4 space-y-3"
              style={{ background: '#f8fafc' }}
              role="log"
              aria-live="polite"
              aria-label="Chat messages"
            >
              {messages.map((msg) => (
                <div
                  key={msg.id}
                  className={`flex gap-2.5 ${msg.role === 'user' ? 'flex-row-reverse' : 'flex-row'}`}
                >
                  {msg.role === 'assistant' && (
                    <div
                      className="w-7 h-7 rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5"
                      style={{ background: 'var(--teal-light)' }}
                    >
                      <Bot size={14} color="var(--teal-mid)" />
                    </div>
                  )}
                  <div className={`max-w-[78%] ${msg.role === 'user' ? 'items-end' : 'items-start'} flex flex-col gap-1`}>
                    <div
                      className="px-4 py-2.5 rounded-2xl text-sm leading-relaxed"
                      style={msg.role === 'user'
                        ? { background: 'var(--primary)', color: 'white', borderBottomRightRadius: '6px' }
                        : { background: 'white', color: 'var(--foreground)', border: '1px solid var(--border)', borderBottomLeftRadius: '6px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }
                      }
                    >
                      {msg.content}
                      {msg.id === 'streaming' && (
                        <span className="inline-block w-1.5 h-4 ml-1 rounded-sm animate-pulse" style={{ background: 'var(--secondary)' }} />
                      )}
                    </div>
                    <span className="text-xs text-gray-400 px-1">{formatTime(msg.timestamp)}</span>
                  </div>
                </div>
              ))}

              {/* Loading indicator */}
              {isLoading && !response && (
                <div className="flex gap-2.5">
                  <div className="w-7 h-7 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: 'var(--teal-light)' }}>
                    <Bot size={14} color="var(--teal-mid)" />
                  </div>
                  <div className="px-4 py-3 rounded-2xl bg-white border border-border" style={{ borderBottomLeftRadius: '6px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
                    <div className="flex gap-1.5 items-center">
                      <span className="w-2 h-2 rounded-full animate-bounce" style={{ background: 'var(--secondary)', animationDelay: '0ms' }} />
                      <span className="w-2 h-2 rounded-full animate-bounce" style={{ background: 'var(--secondary)', animationDelay: '150ms' }} />
                      <span className="w-2 h-2 rounded-full animate-bounce" style={{ background: 'var(--secondary)', animationDelay: '300ms' }} />
                    </div>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Input Area */}
            <div
              className="px-4 py-3 flex-shrink-0"
              style={{ background: 'white', borderTop: '1px solid var(--border)' }}
            >
              <div
                className="flex items-center gap-2 px-3 py-2 rounded-2xl"
                style={{ background: '#f1f5f9', border: '1.5px solid var(--border)' }}
              >
                <input
                  ref={inputRef}
                  type="text"
                  value={isListening ? ui.listening : input}
                  onChange={(e) => !isListening && setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder={ui.placeholder}
                  disabled={isLoading || isListening}
                  className="flex-1 bg-transparent text-sm outline-none text-foreground placeholder-gray-400"
                  aria-label="Chat input"
                  autoComplete="off"
                />
                {/* Voice Input */}
                {speechSupported && (
                  <button
                    onClick={isListening ? stopListening : startListening}
                    disabled={isLoading}
                    className="w-8 h-8 rounded-xl flex items-center justify-center transition-all duration-150 flex-shrink-0"
                    style={{
                      background: isListening ? 'rgba(239,68,68,0.1)' : 'transparent',
                      color: isListening ? '#ef4444' : 'var(--muted-foreground)',
                    }}
                    aria-label={isListening ? 'Stop listening' : ui.voiceOn}
                    title={isListening ? 'Stop listening' : ui.voiceOn}
                  >
                    {isListening ? <MicOff size={16} /> : <Mic size={16} />}
                  </button>
                )}
                {/* Send */}
                <button
                  onClick={handleSend}
                  disabled={isLoading || !input.trim() || isListening}
                  className="w-8 h-8 rounded-xl flex items-center justify-center transition-all duration-150 flex-shrink-0"
                  style={{
                    background: input.trim() && !isLoading ? 'var(--secondary)' : 'var(--border)',
                    color: input.trim() && !isLoading ? 'white' : 'var(--muted-foreground)',
                  }}
                  aria-label={ui.send}
                >
                  <Send size={14} />
                </button>
              </div>
              <p className="text-center text-xs text-gray-400 mt-2">
                {language === 'en' ? 'Powered by ASHA AI · asharecoverygroup.com' : 'Impulsado por ASHA AI · asharecoverygroup.com'}
              </p>
            </div>
          </div>
        )}

        {/* FAB Button */}
        <button
          onClick={() => setIsOpen(prev => !prev)}
          className="relative w-14 h-14 rounded-2xl flex items-center justify-center transition-all duration-200 hover:scale-105 active:scale-95"
          style={{
            background: isOpen ? 'var(--primary)' : 'linear-gradient(135deg, var(--secondary) 0%, #0d9aaa 100%)',
            boxShadow: '0 8px 24px rgba(27,175,191,0.4), 0 3px 8px rgba(10,42,67,0.2)',
          }}
          aria-label={isOpen ? ui.closeChat : ui.openChat}
          aria-expanded={isOpen}
          aria-haspopup="dialog"
        >
          {isOpen ? (
            <ChevronDown size={22} color="white" />
          ) : (
            <MessageCircle size={22} color="white" />
          )}
          {/* Notification dot */}
          {hasNewMessage && !isOpen && (
            <span
              className="absolute -top-1 -right-1 w-4 h-4 rounded-full flex items-center justify-center text-white text-xs font-bold"
              style={{ background: '#ef4444' }}
              aria-label="New message"
            />
          )}
          {/* Pulse ring */}
          {!isOpen && (
            <span
              className="absolute inset-0 rounded-2xl animate-ping opacity-20"
              style={{ background: 'var(--secondary)', animationDuration: '3s' }}
            />
          )}
        </button>
      </div>
    </>
  );
}
